/**
 * An autosaved numeric input (f11.1).
 *
 * Shows the formatted value at rest and the raw decimal while focused. A
 * change is committed on blur or Enter -- exactly one PATCH per cell -- and
 * only when the text is a valid decimal that differs from the stored value.
 * Invalid text is kept, marked, and never sent. While the input has focus
 * the text survives refetches, so a recalculation never eats unsaved typing.
 */

import { useEffect, useRef, useState } from 'react';

import { isDecimalInput, sameDecimal } from '@/features/forecast/forecastView';

interface EditableNumberCellProps {
  /** The stored decimal string; null when unset. */
  value: string | null;
  /** How the value reads at rest. */
  display: string;
  label: string;
  onCommit: (value: string | null) => void;
  disabled?: boolean;
  /** An empty input means "clear" (null) rather than "invalid". */
  allowEmpty?: boolean;
  placeholder?: string;
  title?: string;
  /** f11.6: a validation warning on this cell -- amber outline, still editable. */
  warning?: string;
}

export function EditableNumberCell({
  value,
  display,
  label,
  onCommit,
  disabled = false,
  allowEmpty = false,
  placeholder,
  title,
  warning,
}: EditableNumberCellProps) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [invalid, setInvalid] = useState(false);
  const skipBlur = useRef(false);

  useEffect(() => {
    if (!focused) {
      setDraft(value ?? '');
      setInvalid(false);
    }
  }, [value, focused]);

  function commit() {
    const text = draft.trim();
    if (text === '') {
      if (value == null || value === '') return;
      if (allowEmpty) {
        onCommit(null);
      } else {
        setInvalid(true);
      }
      return;
    }
    if (!isDecimalInput(text)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    if (!sameDecimal(text, value)) onCommit(text);
  }

  const atRest = value == null || value === '' ? '' : display;

  return (
    <td
      className={`col-edit num${disabled ? ' is-locked' : ''}${warning ? ' cell-warning' : ''}`}
      title={invalid ? 'Enter a number' : warning ?? title}
    >
      <input
        className="cell-input"
        inputMode="decimal"
        aria-label={label}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        placeholder={placeholder}
        value={focused || invalid ? draft : atRest}
        onFocus={() => {
          setFocused(true);
          if (!invalid) setDraft(value ?? '');
        }}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          setFocused(false);
          if (skipBlur.current) {
            skipBlur.current = false;
            return;
          }
          commit();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            commit();
            skipBlur.current = true;
            event.currentTarget.blur();
          } else if (event.key === 'Escape') {
            setDraft(value ?? '');
            setInvalid(false);
            skipBlur.current = true;
            event.currentTarget.blur();
          }
        }}
      />
    </td>
  );
}
